import { Component, Input, Output, EventEmitter, forwardRef, CUSTOM_ELEMENTS_SCHEMA, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgIf } from '@angular/common';

@Component({
  selector: 'app-sl-textarea',
  standalone: true,
  imports: [NgIf],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <div class="sl-textarea-wrapper">
      <label *ngIf="label" class="block text-sm font-medium text-gray-700 mb-2">
        {{ label }}
        <span *ngIf="required" class="text-red-500 ml-1">*</span>
      </label>
      
      <sl-textarea
        #textareaElement
        [placeholder]="placeholder"
        [disabled]="disabled"
        [readonly]="readonly"
        [rows]="rows"
        [resize]="resize"
        [size]="size"
        (sl-input)="onInput($event)"
        (sl-change)="onChange($event)"
        (sl-blur)="onBlur()"
        [class.invalid]="invalid">
      </sl-textarea>
      
      <div *ngIf="helpText && invalid" class="mt-1 text-sm text-red-600">
        {{ helpText }}
      </div>
      
      <div *ngIf="showCharCount && maxLength" class="mt-1 text-xs text-gray-500 text-right">
        {{ currentLength }}/{{ maxLength }}
      </div>
    </div>
  `,
  styleUrls: ['./sl-textarea.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SlTextareaComponent),
      multi: true
    }
  ]
})
export class SlTextareaComponent implements ControlValueAccessor, AfterViewInit {
  @ViewChild('textareaElement', { static: false }) textareaElement!: ElementRef;

  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() required: boolean = false;
  @Input() rows: number = 3;
  @Input() resize: 'none' | 'vertical' | 'auto' = 'vertical';
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() invalid: boolean = false;
  @Input() helpText: string = '';
  @Input() maxLength: number = 0;
  @Input() showCharCount: boolean = false;

  @Output() textChange = new EventEmitter<string>();
  @Output() textInput = new EventEmitter<string>();

  private _value: string = '';
  private onChangeCallback = (value: string) => {};
  private onTouchedCallback = () => {};

  get currentLength(): number {
    return this._value?.length || 0;
  }

  ngAfterViewInit(): void {
    // Set initial value after view init
    if (this._value && this.textareaElement) {
      setTimeout(() => {
        this.textareaElement.nativeElement.value = this._value;
      });
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this._value = value || '';
    
    if (this.textareaElement?.nativeElement) {
      this.textareaElement.nativeElement.value = this._value;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: any): void {
    const value = event.target.value;
    
    // Check max length if specified
    if (this.maxLength && value.length > this.maxLength) {
      return; // Don't update if exceeds max length
    }
    
    this._value = value;
    this.onChangeCallback(value);
    this.textInput.emit(value);
  }

  onChange(event: any): void {
    const value = event.target.value;
    this._value = value;
    this.textChange.emit(value);
  }

  onBlur(): void {
    this.onTouchedCallback();
  }

  // Helper method to get current value
  getValue(): string {
    return this._value;
  }

  // Helper method to set focus
  focus(): void {
    if (this.textareaElement?.nativeElement) {
      this.textareaElement.nativeElement.focus();
    }
  }

  // Helper method to clear value
  clear(): void {
    this._value = '';
    this.onChangeCallback('');
    if (this.textareaElement?.nativeElement) {
      this.textareaElement.nativeElement.value = '';
    }
  }
}
